import { type CSSProperties, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router';

import { effectiveSort, useCatalogFilters } from '@/entities/catalog';
import { type Section as SectionRow, useSections } from '@/entities/section';
import { useShortcutBindings, useViewer } from '@/entities/viewer';
import { canCreateTasks, useWorkspace } from '@/entities/workspace';
import { AdvancedCreateDialog } from '@/features/advanced-create';
import { CreateBoardDialog } from '@/features/board-manage';
import { CatalogToolbar } from '@/features/catalog-filters';
import { CREATE_DATABASE, CreateDatabaseDialog } from '@/features/database-catalog';
import { CreateMenu, type CreateItem } from '@/features/create-menu';
import {
  canColorWorkspace,
  WorkspaceAdmin,
  WorkspaceColor,
  WorkspaceTitle,
} from '@/features/workspace-admin';
import { WorkspaceLabels } from '@/features/workspace-labels';
import { ImportBoardDialog } from '@/features/board-portability';
import { SectionNameDialog } from '@/features/workspace-sections';
import { ImportDatabaseDialog } from '@/features/database-portability';
import { WorkspaceTrashDialog } from '@/features/board-recovery';
import { QuickCreateDialog } from '@/features/quick-create';
import { originStateFrom } from '@/shared/lib/origin';
import { can, PERMISSIONS } from '@/shared/lib/permissions';
import { useShortcutHandler } from '@/shared/lib/shortcuts';
import { bindingText } from '@/shared/lib/shortcuts';
import { ErrorState, IconButton, Icons, LoadingState, SplitButton } from '@/shared/ui';

import styles from './WorkspacePage.module.css';
import { WorkspaceObjects } from './WorkspaceObjects';
import { WORKSPACE_OFFERED, WORKSPACE_SORTS } from './workspace-filters';

/**
 * Workspace = a catalog of boards and databases. One primary action (Create), quiet secondary
 * actions and icon links; creation forms are dialogs. The three catalog sections share one search
 * and one set of filters (the same ones as the home dashboard).
 */
export function WorkspacePage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { workspaceId = '' } = useParams();
  const workspace = useWorkspace(workspaceId);
  const viewer = useViewer();
  const bindings = useShortcutBindings();
  const newTaskKeys = bindingText(bindings['new-task']);
  const createTitle = newTaskKeys
    ? `${t('quickCreate.open')} (${newTaskKeys})`
    : t('quickCreate.open');
  const [creatingBoard, setCreatingBoard] = useState(false);
  const [creatingDatabase, setCreatingDatabase] = useState(false);
  const [importingDatabase, setImportingDatabase] = useState(false);
  const [creating, setCreating] = useState(false);
  const [creatingAdvanced, setCreatingAdvanced] = useState(false);
  const [trashOpen, setTrashOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [creatingSection, setCreatingSection] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const { filters, update, reset } = useCatalogFilters();
  const sectionList = useSections(workspaceId);
  // The open section (breadcrumb view) is part of the address, so a reload or a shared link keeps it.
  const folderParam = searchParams.get('folder');
  const folder: SectionRow | null =
    sectionList.data?.items.find((s) => s.id === folderParam) ?? null;
  const focus = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set('folder', id);
    else next.delete('folder');
    setSearch(''); // opening a section leaves the search results
    setSearchParams(next, { replace: false });
  };

  const mayCreate = canCreateTasks(workspace.data);
  useShortcutHandler(
    'new-task',
    mayCreate
      ? () => {
          setCreating(true);
        }
      : null,
  );
  useShortcutHandler('search', () => {
    document.querySelector<HTMLInputElement>('input[type="search"]')?.focus();
  });

  if (workspace.isPending) return <LoadingState />;
  if (workspace.isError) return <ErrorState error={workspace.error} />;
  const canCreate = can(workspace.data.permissions, PERMISSIONS.workspaceCreateBoard);
  const canCreateTask = canCreateTasks(workspace.data);

  const timezone = viewer.status === 'authenticated' ? viewer.user.timezone : null;
  const userId = viewer.status === 'authenticated' ? viewer.user.id : null;
  const isTeam = workspace.data.workspace_type === 'team';
  // A notification may point straight at a management tab (``?tab=requests``).
  const requestedTab = searchParams.get('tab');
  const sections = isTeam
    ? (['all', 'boards', 'databases', 'labels', 'manage'] as const)
    : (['all', 'boards', 'databases'] as const);
  type Section = (typeof sections)[number];
  const requested = searchParams.get('section');
  // "All" (boards and databases together) is the default; Boards / Databases narrow it by type.
  const section: Section =
    requested === 'boards' || requested === 'databases'
      ? requested
      : !isTeam
        ? 'all'
        : requested === 'labels' || requestedTab === 'labels'
          ? 'labels'
          : requested === 'manage' || requestedTab !== null
            ? 'manage'
            : 'all';
  const kinds =
    section === 'boards'
      ? (['board'] as const)
      : section === 'databases'
        ? (['database'] as const)
        : (['board', 'database'] as const);
  const mayCreateDatabase = can(workspace.data.permissions, CREATE_DATABASE);
  const createItems: CreateItem[] = [];
  if (sectionList.data?.can_manage) {
    createItems.push({
      id: 'section',
      label: folder ? t('sections.newSubsection') : t('sections.newSection'),
      icon: <Icons.folder />,
      onSelect: () => {
        setCreatingSection(true);
      },
    });
  }
  if (canCreate) {
    createItems.push({
      id: 'board',
      label: t('workspace.newBoard'),
      icon: <Icons.board />,
      onSelect: () => {
        setCreatingBoard(true);
      },
    });
  }
  if (mayCreateDatabase) {
    createItems.push({
      id: 'database',
      label: t('databases.new'),
      icon: <Icons.layers />,
      onSelect: () => {
        setCreatingDatabase(true);
      },
    });
    createItems.push({
      id: 'database-import',
      label: t('databases.portability.import.action'),
      icon: <Icons.import />,
      onSelect: () => {
        setImportingDatabase(true);
      },
    });
  }

  return (
    <div className={styles.page}>
      <nav className={styles.crumbs} aria-label={t('workspace.toHomeHint')}>
        <Link to="/app" className={styles.homeLink} title={t('workspace.toHomeHint')}>
          <Icons.chevronLeft /> {t('workspace.toHome')}
        </Link>
      </nav>
      <header
        className={`${styles.header} ${workspace.data.color ? styles.tinted : ''}`}
        data-testid="workspace-header"
        data-color={workspace.data.color ?? undefined}
        style={
          workspace.data.color
            ? ({ '--workspace-color': workspace.data.color } as CSSProperties)
            : undefined
        }
      >
        <div className={styles.titles}>
          <WorkspaceTitle workspace={workspace.data} />
          <p className={styles.subtitle}>{t('workspace.subtitle')}</p>
        </div>
        <div className={styles.actions} role="group" aria-label={t('workspace.actions')}>
          <CreateMenu items={createItems} />
          {canCreateTask && (
            <SplitButton
              tone="quiet"
              label={t('quickCreate.open')}
              title={createTitle}
              onMain={() => {
                setCreating(true);
              }}
              menuLabel={t('boardHeader.createMenu')}
              items={[
                {
                  label: t('boardHeader.quick'),
                  icon: <Icons.plus />,
                  onSelect: () => {
                    setCreating(true);
                  },
                },
                {
                  label: t('boardHeader.advanced'),
                  icon: <Icons.edit />,
                  onSelect: () => {
                    setCreatingAdvanced(true);
                  },
                },
              ]}
            />
          )}
          {canCreate && (
            <IconButton
              label={t('portability.import.open')}
              icon={<Icons.import />}
              onClick={() => {
                setImportOpen(true);
              }}
            />
          )}
          {canColorWorkspace(workspace.data) && <WorkspaceColor workspace={workspace.data} />}
          <Link
            to={`/app/workspaces/${workspaceId}/calendar`}
            className={styles.iconLink}
            aria-label={t('calendar.open')}
            title={t('workspace.openCalendar')}
          >
            <Icons.calendar />
          </Link>
          {can(workspace.data.permissions, PERMISSIONS.workspaceManageBoardTrash) && (
            <IconButton
              label={t('workspaceTrash.open')}
              icon={<Icons.trash />}
              onClick={() => {
                setTrashOpen(true);
              }}
            />
          )}
        </div>
      </header>

      <nav className={styles.sections} aria-label={t('workspaceAdmin.sections')}>
        {sections.map((value) => (
          <button
            key={value}
            type="button"
            className={`${styles.sectionTab} ${section === value ? styles.sectionTabOn : ''}`}
            aria-pressed={section === value}
            onClick={() => {
              const next = new URLSearchParams(searchParams);
              next.delete('tab');
              if (value === 'all') next.delete('section');
              else next.set('section', value);
              setSearchParams(next, { replace: true });
            }}
          >
            {t(`workspace.nav.${value}`)}
          </button>
        ))}
      </nav>

      {isTeam && section === 'manage' && <WorkspaceAdmin workspace={workspace.data} />}
      {isTeam && section === 'labels' && <WorkspaceLabels workspace={workspace.data} />}
      {(section === 'all' || section === 'boards' || section === 'databases') && (
        <section aria-labelledby="objects-title" className={styles.boards}>
          <h2 id="objects-title" className={styles.sectionTitle}>
            {t(`workspace.nav.${section}`)}
          </h2>
          <CatalogToolbar
            query={search}
            onQuery={setSearch}
            searchLabel={t(
              section === 'boards'
                ? 'boards.search'
                : section === 'databases'
                  ? 'databases.search'
                  : 'workspace.objectsSearch',
            )}
            filters={filters}
            onChange={update}
            onReset={reset}
            offered={WORKSPACE_OFFERED}
            sorts={WORKSPACE_SORTS}
            sort={effectiveSort(filters, WORKSPACE_SORTS, 'manual')}
          />
          <WorkspaceObjects
            workspaceId={workspaceId}
            kinds={kinds}
            query={search}
            filters={filters}
            locale={i18n.language}
            userId={userId}
            focusId={folder?.id ?? null}
            onFocus={focus}
          />
        </section>
      )}

      {creatingBoard && (
        <CreateBoardDialog
          workspaceId={workspaceId}
          sectionId={folder?.id}
          onCreated={(board) => {
            setCreatingBoard(false);
            void navigate(`/app/boards/${board.id}`, { state: originStateFrom(location) });
          }}
          onClose={() => {
            setCreatingBoard(false);
          }}
        />
      )}
      {creatingDatabase && (
        <CreateDatabaseDialog
          workspaces={[workspace.data]}
          workspaceId={workspaceId}
          sectionId={folder?.id}
          onClose={() => {
            setCreatingDatabase(false);
          }}
          onCreated={(database) => {
            setCreatingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {creatingSection && (
        <SectionNameDialog
          workspaceId={workspaceId}
          parent={folder}
          onClose={() => {
            setCreatingSection(false);
          }}
        />
      )}
      {importingDatabase && (
        <ImportDatabaseDialog
          workspaces={[workspace.data]}
          workspaceId={workspaceId}
          onClose={() => {
            setImportingDatabase(false);
          }}
          onImported={(database) => {
            setImportingDatabase(false);
            void navigate(`/app/databases/${database.id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {importOpen && (
        <ImportBoardDialog
          workspaceId={workspaceId}
          onClose={() => {
            setImportOpen(false);
          }}
          onImported={(result) => {
            setImportOpen(false);
            void navigate(`/app/boards/${result.board_id}`, { state: originStateFrom(location) });
          }}
        />
      )}
      {trashOpen && (
        <WorkspaceTrashDialog
          workspaceId={workspaceId}
          onClose={() => {
            setTrashOpen(false);
          }}
        />
      )}
      {creatingAdvanced && (
        <AdvancedCreateDialog
          workspaceId={workspaceId}
          timezone={timezone}
          onClose={() => {
            setCreatingAdvanced(false);
          }}
        />
      )}
      {creating && (
        <QuickCreateDialog
          workspaceId={workspaceId}
          timezone={timezone}
          onClose={() => {
            setCreating(false);
          }}
        />
      )}
    </div>
  );
}
